import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getMaterialTraceabilityAllData,
  getListMaterialCategory,
} from '../../../redux/Project/materialsData/action';
import { fetchProjectList } from '../../../redux/Project/action';
import {
  clearAllPipeDetails,
  getAllPipeDetails,
  getListManufacturingCategory,
} from '../../../redux/Project/setupData/action';
import { PipeDashboardTable } from './PipeDashboardTable';
import { DocumentDownloadModal } from '../../project/components/dashboard/specifications/DocumentDownloadModal';
import { MaterialDocumentUploadModal } from '../../project/components/dashboard/materials/MaterialDocumentUploadModal';
import { UpdateMaterialDialog } from '../../project/components/dashboard/materials/UpdateMaterialDialog';
import { DeleteConfirmationDialog, DeleteItem } from '../../../components/ui/DeleteConfirmationDialog';
import { Notification } from '../../../components/ui/custom-notification';
import { extractPipeSizesFromPipeDetails } from '../../project/components/dashboard/materials/extractPipeSizesFromPipeDetails';
import {
  deleteMaterials,
  DeleteMaterialDocument,
} from '../../../redux/Project/materialSaga';
import type { Project } from '../../project/types/project';
import { DocumentTag } from '../../project/types';
import { useUserPermissionsAfterLogin } from '../../admin/hooks/useUserPermissionsAfterLogin';
import {
  buildProjectNameByIdMap,
  findPipeSetupForMaterial,
  getProjectIdFromMaterialRow,
  pipeDetailsBelongToProject,
  withResolvedAnalysisProjectNames,
} from '../utils/analysisMaterialProjectContext';
import {
  buildManufacturerNameByIdMap,
  withResolvedManufacturerLabels,
} from '../utils/analysisManufacturerContext';
import { isWarehouseMaterialRow } from '../utils/warehouseMaterialPayload';
import {
  buildAccessibleProjectIdSet,
  filterMaterialsByAccessibleProjects,
} from '../utils/filterMaterialsByAccessibleProjects';
import { AnalysisWarehouseMaterialDialog } from './AnalysisWarehouseMaterialDialog';
import { AnalysisWarehouseDocumentUploadModal } from './AnalysisWarehouseDocumentUploadModal';
import { PipeGroupByControl } from './PipeGroupByControl';
import { PipeFilters } from '../../project/components/dashboard/materials/tabs/Filters/PipeFilters';
import { usePipeFilters } from '../../project/components/dashboard/materials/tabs/Filters/hooks/usePipeFilters';
import type { MaterialData } from '../../project/types/materials';
import { AnalysisAddMaterial, type AnalysisAddFitterSeed } from './AnalysisAddMaterial';
import { PipeInventoryFitterPanel } from './PipeInventoryFitterPanel';
import { PipeInventoryViewToggle, type PipeInventoryViewMode } from './PipeInventoryViewToggle';
import { isProjectPipeFitterEnabled } from '../utils/isProjectPipeFitterEnabled';
import { PipeSegmentationModal } from '../../project/components/dashboard/materials/tabs/PipeSegmentationModal';
import { useTableSort } from '../../../hooks/useTableSort';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { cn } from '../../../lib/utils';
import {
  DEFAULT_PIPE_GROUP_BY,
  normalizePipeGroupBy,
  parseStoredPipeGroupBy,
  PIPE_GROUP_BY_STORAGE_KEY,
  type PipeGroupField,
} from '../utils/groupPipeMaterials';
import { logPipeInventoryMaterialDeleted } from '../utils/pipeInventoryAudit';

function transformDocumentsForModal(material: Record<string, unknown>) {
  const documents = (material.documents as Record<string, unknown>[]) || [];
  const transformedDocs = documents.flatMap((doc) => {
    const materials = doc.materials as Record<string, unknown>[] | undefined;
    if (materials && Array.isArray(materials)) {
      return materials.map((mat) => ({
        id: mat.materialTraceabilityId ?? Math.random().toString(),
        fileName: mat.fileName,
        fileUrl: mat.fileUrl,
        uploadDate: mat.uploadedAt ?? mat.createdAt,
        type:
          Array.isArray(mat.tags) && mat.tags.length > 0
            ? (mat.tags as Array<{ tagName?: string }>)
                .map((tag) => tag.tagName?.toLowerCase().replace('_', ' '))
                .join(', ')
            : 'document',
      }));
    }
    const tags = doc.tags as string[] | undefined;
    return [
      {
        id: doc.id ?? Math.random().toString(),
        fileName: doc.fileName,
        fileUrl: doc.fileUrl,
        uploadDate: doc.uploadedAt,
        type: Array.isArray(tags) ? tags.join(', ') : 'document',
      },
    ];
  });

  return transformedDocs.sort((a, b) => {
    const dateA = a.uploadDate ? new Date(String(a.uploadDate)).getTime() : 0;
    const dateB = b.uploadDate ? new Date(String(b.uploadDate)).getTime() : 0;
    return dateB - dateA;
  });
}

/** Load full pipe list for client-side filters (same pattern as project Materials pipe tab). */
const CLIENT_FETCH_PAGE_SIZE = 10000;

export const PipeDashboardSummary: React.FC = () => {
  const {
    materialTraceabilityAllData,
    materialTraceabilityAllDataLoading,
    materialTraceabilityAllDataError,
  } = useSelector((state: { materialsData?: Record<string, unknown> }) =>
    state.materialsData || {}
  ) as {
    materialTraceabilityAllData: Record<string, unknown[]>;
    materialTraceabilityAllDataLoading: boolean;
    materialTraceabilityAllDataError: string | null;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [sortBy, setSortBy] = useState('material_id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedMaterialRow, setSelectedMaterialRow] = useState<Record<string, unknown> | null>(
    null
  );
  const [isSegmentationModalOpen, setIsSegmentationModalOpen] = useState(false);
  const [segmentationMaterial, setSegmentationMaterial] = useState<Record<string, unknown> | null>(
    null
  );
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState<MaterialData | null>(null);
  const [itemToDelete, setItemToDelete] = useState<MaterialData | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<Record<string, unknown> | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [deletingMaterialIds, setDeletingMaterialIds] = useState<Set<string>>(new Set());
  const [actionPipeSizesData, setActionPipeSizesData] = useState<Array<Record<string, unknown>>>(
    []
  );
  const [actionSelectedPipe, setActionSelectedPipe] = useState<Record<string, unknown> | null>(
    null
  );
  const [actionUnit, setActionUnit] = useState('in');
  const [pendingRowAction, setPendingRowAction] = useState<{
    row: MaterialData;
    kind: 'upload' | 'edit';
  } | null>(null);
  const [contextProjectId, setContextProjectId] = useState<string | null>(null);
  const [warehouseEditOpen, setWarehouseEditOpen] = useState(false);
  const [warehouseEditMaterial, setWarehouseEditMaterial] = useState<MaterialData | null>(null);
  const [warehouseUploadOpen, setWarehouseUploadOpen] = useState(false);
  const [warehouseUploadMaterial, setWarehouseUploadMaterial] = useState<MaterialData | null>(null);
  const [groupByEnabled, setGroupByEnabled] = useState(true);
  const [inventoryViewMode, setInventoryViewMode] = useState<PipeInventoryViewMode>('material');
  const [fitterAddSeed, setFitterAddSeed] = useState<AnalysisAddFitterSeed | null>(null);
  const handleFitterSeedConsumed = useCallback(() => setFitterAddSeed(null), []);
  const handleAddFromFitter = useCallback(
    (seed: { projectId: string; pipeNo: string; pipePipeId?: string }) => {
      setFitterAddSeed({
        projectId: seed.projectId,
        pipeNo: seed.pipeNo,
        pipePipeId: seed.pipePipeId,
      });
    },
    []
  );
  /** Pipe Inventory table display unit for OD / wall / length (DB is inches). */
  const [tableDisplayUnit, setTableDisplayUnit] = useState<'in' | 'mm'>('in');
  const [groupByFields, setGroupByFields] = useState<PipeGroupField[]>(() => {
    if (typeof window === 'undefined') return [...DEFAULT_PIPE_GROUP_BY];
    const stored = parseStoredPipeGroupBy(window.localStorage.getItem(PIPE_GROUP_BY_STORAGE_KEY));
    return stored && stored.length > 0
      ? normalizePipeGroupBy(stored)
      : [...DEFAULT_PIPE_GROUP_BY];
  });

  const handleGroupByFieldsChange = useCallback((fields: PipeGroupField[]) => {
    const normalized = normalizePipeGroupBy(fields);
    setGroupByFields(normalized);
    try {
      window.localStorage.setItem(PIPE_GROUP_BY_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // ignore quota / private mode
    }
  }, []);

  const { canAccessAdminFeatures, userPermissions, hasPermission, userRole } =
    useUserPermissionsAfterLogin();
  const userId = userPermissions?.user_id ?? null;
  const canAddOrEdit = hasPermission('edit_pipe_inventory');
  const canEdit = canAddOrEdit;
  const canDelete = hasPermission('delete_pipe_inventory');
  /** Role "user" only sees projects they are assigned to; other roles unchanged. */
  const restrictProjectsToUserAccess = userRole === 'user';

  const projectList = useSelector(
    (state: { project?: { ProjectList?: Project[] } }) => state.project?.ProjectList ?? []
  ) as Project[];

  const accessibleProjectIds = useMemo(
    () => buildAccessibleProjectIdSet(projectList, restrictProjectsToUserAccess),
    [projectList, restrictProjectsToUserAccess]
  );

  /** Role "user": project-scoped actions only when assigned to at least one project. */
  const hasAssignedProjectAccess =
    !restrictProjectsToUserAccess || projectList.length > 0;

  const showFitterTab = useMemo(() => {
    if (!restrictProjectsToUserAccess) return true;
    return projectList.some((p) => isProjectPipeFitterEnabled(p));
  }, [projectList, restrictProjectsToUserAccess]);

  const showAddProjectMaterial = canAddOrEdit && hasAssignedProjectAccess;

  const { pipeDetails, isPipeDetailsLoading, manufacturingCategories } = useSelector(
    (state: {
      setupData?: {
        pipeDetails?: Record<string, unknown>;
        isPipeDetailsLoading?: boolean;
        manufacturingCategories?: Array<Record<string, unknown>>;
      };
    }) => state.setupData ?? {}
  );

  const materialCategories = useSelector(
    (state: { materialsData?: { materialCategories?: DocumentTag[] } }) =>
      state.materialsData?.materialCategories
  );

  const rawMaterials = useMemo(() => {
    const store = materialTraceabilityAllData as Record<string, unknown> | undefined;
    // Redux stores API category map at top level: { pipes, forge, ... }
    const direct = store?.pipes;
    const nested = (store?.data as Record<string, unknown> | undefined)?.pipes;
    const list = (Array.isArray(direct)
      ? direct
      : Array.isArray(nested)
        ? nested
        : []) as MaterialData[];

    return filterMaterialsByAccessibleProjects(
      list as unknown as Array<Record<string, unknown>>,
      accessibleProjectIds,
      projectList
    ) as MaterialData[];
  }, [materialTraceabilityAllData, accessibleProjectIds, projectList]);

  // Analysis-only: resolve missing names from ProjectList via projectId
  const projectNameById = useMemo(
    () => buildProjectNameByIdMap(projectList),
    [projectList]
  );

  const manufacturerNameById = useMemo(
    () =>
      buildManufacturerNameByIdMap(
        Array.isArray(manufacturingCategories) ? manufacturingCategories : []
      ),
    [manufacturingCategories]
  );

  const pipeMaterialsForFilters = useMemo(() => {
    const withProjects = withResolvedAnalysisProjectNames(rawMaterials, projectNameById);
    return withResolvedManufacturerLabels(withProjects, manufacturerNameById);
  }, [rawMaterials, projectNameById, manufacturerNameById]);

  const {
    searchQuery: pipeSearchQuery,
    setSearchQuery: setPipeSearchQuery,
    filters: pipeFilters,
    onFilterChange: onPipeFilterChange,
    uniquePipeNumbers,
    uniquePipeNRIs,
    uniquePipeProjectStages,
    uniqueHeatNumbers: uniquePipeHeatNumbers,
    uniqueOuterDiameters,
    uniqueWallThicknesses,
    uniqueProjectNames,
    filteredMaterials: pipeFilteredMaterials,
    hasActiveFilters: hasActivePipeFilters,
    onResetFilters: onResetPipeFilters,
  } = usePipeFilters({ materials: pipeMaterialsForFilters });

  const filteredMaterials = pipeFilteredMaterials as unknown as Record<string, unknown>[];

  const { sortedData: sortedFilteredMaterials, handleSort, getSortDirection } = useTableSort(
    filteredMaterials,
    'materialId'
  );

  const tableTotalItems = sortedFilteredMaterials.length;

  const tableData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedFilteredMaterials.slice(start, start + pageSize);
  }, [sortedFilteredMaterials, currentPage, pageSize]);

  const buildPayload = useCallback(() => {
    return {
      projectNames: [],
      pipeFilters: [],
      pageSize: CLIENT_FETCH_PAGE_SIZE,
      page: 1,
      sortBy,
      sortOrder,
      materialCategory: 'Pipe' as const,
      pipeFitterOnly: false,
    };
  }, [sortBy, sortOrder]);

  const dispatch = useDispatch();

  const fetchTableData = useCallback(() => {
    dispatch(getMaterialTraceabilityAllData(buildPayload()));
  }, [dispatch, buildPayload]);

  /** Refresh table after add / edit / delete / status changes. */
  const refreshDashboard = useCallback(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    dispatch(getListMaterialCategory({}));
    dispatch(getListManufacturingCategory({}));
  }, [dispatch]);

  // Role "user": always load only assigned projects (ignore any unscoped list already in Redux).
  useEffect(() => {
    if (!restrictProjectsToUserAccess || !userId) return;
    dispatch(fetchProjectList({ userId }));
  }, [dispatch, restrictProjectsToUserAccess, userId]);

  // Other roles: keep existing shared ProjectList behavior.
  useEffect(() => {
    if (restrictProjectsToUserAccess) return;
    if (projectList.length > 0) return;
    if (canAccessAdminFeatures()) {
      dispatch(fetchProjectList({}));
    } else if (userId) {
      dispatch(fetchProjectList({ userId }));
    }
  }, [
    dispatch,
    projectList.length,
    canAccessAdminFeatures,
    userId,
    restrictProjectsToUserAccess,
  ]);

  useEffect(() => {
    if (!showFitterTab && inventoryViewMode === 'fitter') {
      setInventoryViewMode('material');
    }
  }, [showFitterTab, inventoryViewMode]);

  useEffect(() => {
    if (materialCategories && materialCategories.length > 0) {
      const uniqueTags = materialCategories
        .filter((tag, index, self) => index === self.findIndex((t) => t.tags_name === tag.tags_name))
        .sort((a, b) => a.tags_name.localeCompare(b.tags_name));
      setDocumentTags(uniqueTags);
    }
  }, [materialCategories]);

  const openDeleteConfirm = useCallback((row: Record<string, unknown>) => {
    setItemToDelete(row as MaterialData);
    setIsDeleteModalOpen(true);
  }, []);

  const openRowAction = useCallback(
    (row: Record<string, unknown>, kind: 'upload' | 'edit') => {
      const materialRow = row as MaterialData;

      const projectId = getProjectIdFromMaterialRow(row, projectList);
      if (!projectId) {
        Notification.warning(
          'Project unknown',
          'This material row has no project information. Upload and edit require a linked project.'
        );
        return;
      }
      const proj = projectList.find((p) => String(p.projectId) === String(projectId));
      if (!proj) {
        Notification.warning('Project not found', 'Could not load project for this material.');
        return;
      }
      setActionUnit(proj.unit || 'in');
      setPendingRowAction({ row: materialRow, kind });
      setContextProjectId(projectId);

      if (pipeDetailsBelongToProject(pipeDetails, projectId)) {
        const extracted = extractPipeSizesFromPipeDetails(pipeDetails as Record<string, unknown>, proj);
        const selectedPipe = findPipeSetupForMaterial(extracted, row);
        if (!selectedPipe) {
          setPendingRowAction(null);
          Notification.warning('Pipe setup not found', 'No matching pipe setup for this material.');
          return;
        }
        setActionPipeSizesData(extracted);
        setActionSelectedPipe(selectedPipe);
        if (kind === 'upload') {
          setSelectedMaterialRow(row);
          setIsUploadModalOpen(true);
        } else {
          setItemToUpdate(materialRow);
          setIsUpdateModalOpen(true);
        }
        setPendingRowAction(null);
        return;
      }

      dispatch(clearAllPipeDetails());
      dispatch(getAllPipeDetails({ projectId: Number(projectId) }));
    },
    [projectList, pipeDetails, dispatch]
  );

  useEffect(() => {
    if (!pendingRowAction || !contextProjectId || isPipeDetailsLoading) return;
    if (!pipeDetailsBelongToProject(pipeDetails, contextProjectId)) return;

    const proj = projectList.find((p) => String(p.projectId) === String(contextProjectId));
    if (!proj) {
      setPendingRowAction(null);
      Notification.warning('Project not found', 'Could not load project for this material.');
      return;
    }

    const extracted = extractPipeSizesFromPipeDetails(pipeDetails as Record<string, unknown>, proj);
    const selectedPipe = findPipeSetupForMaterial(extracted, pendingRowAction.row as Record<string, unknown>);
    if (!selectedPipe) {
      setPendingRowAction(null);
      Notification.warning('Pipe setup not found', 'No matching pipe setup for this material.');
      return;
    }

    setActionPipeSizesData(extracted);
    setActionSelectedPipe(selectedPipe);

    const { row, kind } = pendingRowAction;
    if (kind === 'upload') {
      setSelectedMaterialRow(row as Record<string, unknown>);
      setIsUploadModalOpen(true);
    } else if (kind === 'edit') {
      setItemToUpdate(row);
      setIsUpdateModalOpen(true);
    }
    setPendingRowAction(null);
  }, [
    pendingRowAction,
    contextProjectId,
    isPipeDetailsLoading,
    pipeDetails,
    projectList,
  ]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pipeSearchQuery, pipeFilters]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleDocumentClick = useCallback((row: Record<string, unknown>) => {
    setSelectedMaterialRow(row);
    setIsDocumentModalVisible(true);
  }, []);

  const handleSegmentationClick = useCallback((row: Record<string, unknown>) => {
    setSegmentationMaterial(row);
    setIsSegmentationModalOpen(true);
  }, []);

  const handleSegmentationSaved = useCallback(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  const handleDocumentDownload = useCallback((document: { fileUrl?: string }) => {
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleUploadClick = useCallback(
    (row: Record<string, unknown>) => {
      if (isWarehouseMaterialRow(row)) {
        setWarehouseUploadMaterial(row as MaterialData);
        setWarehouseUploadOpen(true);
        return;
      }
      openRowAction(row, 'upload');
    },
    [openRowAction]
  );

  const handleEditClick = useCallback(
    (row: Record<string, unknown>) => {
      if (isWarehouseMaterialRow(row)) {
        setWarehouseEditMaterial(row as MaterialData);
        setWarehouseEditOpen(true);
        return;
      }
      openRowAction(row, 'edit');
    },
    [openRowAction]
  );

  const handleDeleteClick = useCallback(
    (row: Record<string, unknown>) => {
      // Warehouse and project materials both delete by materialId only (no pipe setup).
      openDeleteConfirm(row);
    },
    [openDeleteConfirm]
  );

  const handleConfirmDeleteMaterial = async () => {
    if (!itemToDelete?.materialId) return;
    const idKey = String(itemToDelete.materialId);
    try {
      setDeleteLoading(true);
      setDeletingMaterialIds((prev) => new Set(prev).add(idKey));
      const res = await deleteMaterials([itemToDelete.materialId], itemToDelete.materialCategory);
      if (res) {
        logPipeInventoryMaterialDeleted(itemToDelete);
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
        refreshDashboard();
      }
    } finally {
      setDeleteLoading(false);
      setDeletingMaterialIds((prev) => {
        const next = new Set(prev);
        next.delete(idKey);
        return next;
      });
    }
  };

  const handleUpdateMaterial = async () => {
    refreshDashboard();
  };

  const handleUploadComplete = () => {
    refreshDashboard();
  };

  const handleDeleteDocument = (doc: Record<string, unknown>) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);
    try {
      const materialTraceabilityId = documentToDelete.id ?? documentToDelete.materialTraceabilityId;
      if (!materialTraceabilityId) {
        setDeleteDocumentLoading(false);
        return;
      }
      const deleteResponse = await DeleteMaterialDocument({
        materialDocsIds: [Number(materialTraceabilityId)],
      });
      if (deleteResponse) {
        setIsDocumentDeleteModalOpen(false);
        setDocumentToDelete(null);
        setIsDocumentModalVisible(false);
        setSelectedMaterialRow(null);
        refreshDashboard();
      }
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  const renderDeleteItemDetails = (item: DeleteItem) => (
    <div className="text-sm text-gray-700 py-2 border-b border-gray-200 last:border-b-0">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>• NRI: {item.details?.pipeNRI}</div>
        <div>• Pipe No: {item.details?.pipeNo}</div>
        <div>• Heat Number: {item.details?.pipeHeatNumber}</div>
        <div>• Length: {item.details?.pipeLength}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {materialTraceabilityAllDataError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {materialTraceabilityAllDataError}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PipeInventoryViewToggle
            value={inventoryViewMode}
            onChange={setInventoryViewMode}
            showFitterTab={showFitterTab}
          />
          <AnalysisAddMaterial
            onMaterialAdded={refreshDashboard}
            hideToolbar={inventoryViewMode === 'fitter' || !canAddOrEdit}
            showAddProjectMaterial={showAddProjectMaterial}
            fitterSeed={canAddOrEdit && hasAssignedProjectAccess ? fitterAddSeed : null}
            onFitterSeedConsumed={handleFitterSeedConsumed}
          />
        </div>

        {inventoryViewMode === 'fitter' && showFitterTab ? (
          <div className="mt-1">
            <PipeInventoryFitterPanel
              projectList={projectList}
              materials={rawMaterials as Array<Record<string, unknown>>}
              onAddFromFitter={
                canAddOrEdit && hasAssignedProjectAccess ? handleAddFromFitter : undefined
              }
            />
          </div>
        ) : (
          <div className="mt-1 space-y-3">
            <PipeFilters
              searchQuery={pipeSearchQuery}
              onSearchChange={setPipeSearchQuery}
              filters={pipeFilters}
              onFilterChange={onPipeFilterChange}
              uniquePipeNumbers={uniquePipeNumbers}
              uniquePipeNRIs={uniquePipeNRIs}
              uniquePipeProjectStages={uniquePipeProjectStages}
              uniqueHeatNumbers={uniquePipeHeatNumbers}
              uniqueOuterDiameters={uniqueOuterDiameters}
              uniqueWallThicknesses={uniqueWallThicknesses}
              uniqueProjectNames={uniqueProjectNames}
              hasActiveFilters={hasActivePipeFilters}
              filteredCount={pipeFilteredMaterials.length}
              onResetFilters={onResetPipeFilters}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <PipeGroupByControl
                  groupByFields={groupByFields}
                  onChange={handleGroupByFieldsChange}
                  enabled={groupByEnabled}
                  onEnabledChange={setGroupByEnabled}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 rounded-lg border border-[#E2E5EA] bg-white px-3 py-1.5">
                <Label
                  htmlFor="pipe-inventory-unit"
                  className={cn(
                    'cursor-pointer text-xs font-semibold',
                    tableDisplayUnit === 'in' ? 'text-blueprint-blue' : 'text-heritage-grey'
                  )}
                >
                  Inch
                </Label>
                <Switch
                  id="pipe-inventory-unit"
                  checked={tableDisplayUnit === 'mm'}
                  onCheckedChange={(checked: boolean) => setTableDisplayUnit(checked ? 'mm' : 'in')}
                  aria-label="Toggle display unit between Inch and MM"
                />
                <Label
                  htmlFor="pipe-inventory-unit"
                  className={cn(
                    'cursor-pointer text-xs font-semibold',
                    tableDisplayUnit === 'mm' ? 'text-blueprint-blue' : 'text-heritage-grey'
                  )}
                >
                  MM
                </Label>
              </div>
            </div>

            <PipeDashboardTable
              data={tableData}
              totalItems={tableTotalItems}
              currentPage={currentPage}
              pageSize={pageSize}
              loading={materialTraceabilityAllDataLoading}
              displayUnit={tableDisplayUnit}
              onSort={handleSort}
              getSortDirection={getSortDirection}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onDocumentClick={handleDocumentClick}
              onUploadClick={handleUploadClick}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              onSegmentationClick={handleSegmentationClick}
              canEdit={canEdit}
              canDelete={canDelete}
              deletingMaterialIds={deletingMaterialIds}
              category="Pipe"
              groupByEnabled={groupByEnabled}
              groupByFields={groupByFields}
            />
          </div>
        )}
      </div>

      {isSegmentationModalOpen && segmentationMaterial ? (
        <PipeSegmentationModal
          isOpen={isSegmentationModalOpen}
          onClose={() => {
            setIsSegmentationModalOpen(false);
            setSegmentationMaterial(null);
          }}
          pipeId={String(
            (segmentationMaterial.details as Record<string, unknown> | undefined)?.pipePipeId ?? 'N/A'
          )}
          heatNumber={String(
            (segmentationMaterial.details as Record<string, unknown> | undefined)?.pipeHeatNumber ??
              'N/A'
          )}
          pipeNo={String(
            (segmentationMaterial.details as Record<string, unknown> | undefined)?.pipeNo ?? 'N/A'
          )}
          onSave={handleSegmentationSaved}
          selectedMaterial={segmentationMaterial}
        />
      ) : null}

      {selectedMaterialRow ? (
        <DocumentDownloadModal
          isOpen={isDocumentModalVisible}
          onClose={() => {
            setIsDocumentModalVisible(false);
            setSelectedMaterialRow(null);
          }}
          documents={transformDocumentsForModal(selectedMaterialRow)}
          onDownload={handleDocumentDownload}
          onDelete={handleDeleteDocument}
          title="Pipe Material Documents"
          canDelete={canDelete}
        />
      ) : null}

      <MaterialDocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setSelectedMaterialRow(null);
        }}
        onUpload={handleUploadComplete}
        title="Upload Material Document"
        acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
        multiple={true}
        maxFiles={1}
        buttonText="Select Document"
        label="Document"
        description="Upload material documents (PDF, Excel, DOC, etc...)"
        material={(selectedMaterialRow as MaterialData) || undefined}
        documentTags={documentTags}
        setDocumentTags={setDocumentTags}
        materialType="Pipe"
        selectedPipeSize={actionSelectedPipe ?? actionPipeSizesData}
      />

      {isUpdateModalOpen && itemToUpdate && actionSelectedPipe ? (
        <UpdateMaterialDialog
          isOpen={isUpdateModalOpen}
          onClose={() => {
            setIsUpdateModalOpen(false);
            setItemToUpdate(null);
          }}
          onUpdate={handleUpdateMaterial}
          material={itemToUpdate}
          project={actionPipeSizesData as never}
          unit={actionUnit}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          selectedPipe={actionSelectedPipe}
          pipeInventoryMode
        />
      ) : null}

      <DeleteConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDeleteMaterial}
        itemType="Material"
        itemName={itemToDelete?.details?.pipeNRI}
        itemToDelete={itemToDelete}
        detailsRenderer={renderDeleteItemDetails}
        isLoading={deleteLoading}
      />

      <AnalysisWarehouseMaterialDialog
        isOpen={warehouseEditOpen}
        onClose={() => {
          setWarehouseEditOpen(false);
          setWarehouseEditMaterial(null);
        }}
        onSaved={refreshDashboard}
        material={warehouseEditMaterial}
        documentTags={documentTags}
      />

      <AnalysisWarehouseDocumentUploadModal
        isOpen={warehouseUploadOpen}
        onClose={() => {
          setWarehouseUploadOpen(false);
          setWarehouseUploadMaterial(null);
        }}
        onUploaded={refreshDashboard}
        material={warehouseUploadMaterial}
        documentTags={documentTags}
      />

      <DeleteConfirmationDialog
        isOpen={isDocumentDeleteModalOpen}
        onClose={() => {
          setIsDocumentDeleteModalOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleConfirmDeleteDocument}
        isLoading={deleteDocumentLoading}
        itemToDelete={documentToDelete}
        itemType="Material Document"
        itemName={
          documentToDelete?.fileName != null
            ? String(documentToDelete.fileName)
            : documentToDelete?.file_name != null
              ? String(documentToDelete.file_name)
              : ''
        }
        title="Delete Material Document"
      />
    </div>
  );
};
